import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { endOfDay, parseISO } from 'date-fns'
import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ArrowUp } from '@factorialco/f0-react/icons/app'
import {
  BankingValueObjectsPaymentRequestStatusEnum,
  PaymentRequestFieldsFragment,
  PaymentRequestsDataCollectionDocument,
  PaymentRequestsPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { PayPaymentRequestsInBulkModal } from 'modules/banking/components/PayPaymentRequestsInBulkModal'
import { AllPaymentRequestsInsightCards } from 'modules/banking/components/TreasuryInsightCard'
import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { useTreasuryDateRange } from 'modules/finance/hooks/useTreasuryDateRange'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FEATURE_FLAG_NAME = 'dev_banking_payment_requests'

const VALID_STATUSES: BankingValueObjectsPaymentRequestStatusEnum[] = [
  'init',
  'in_payment',
  'in_progress',
  'paid',
]

const NON_PAID_STATUSES: BankingValueObjectsPaymentRequestStatusEnum[] = [
  'init',
  'in_payment',
  'in_progress',
]
const validStatusValues = new Set<string>(VALID_STATUSES)

const filterValidStatuses = (
  statuses: string[] | undefined
): BankingValueObjectsPaymentRequestStatusEnum[] | undefined => {
  if (!statuses?.length) return undefined
  const filtered = statuses.filter((s): s is BankingValueObjectsPaymentRequestStatusEnum =>
    validStatusValues.has(s)
  )
  return filtered.length > 0 ? filtered : undefined
}

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning'

const getStatusColor = (status: BankingValueObjectsPaymentRequestStatusEnum): StatusColor => {
  const statusColorMap: Record<BankingValueObjectsPaymentRequestStatusEnum, StatusColor> = {
    init: 'warning',
    in_payment: 'info',
    in_progress: 'info',
    paid: 'positive',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getStatusLabel = (status: BankingValueObjectsPaymentRequestStatusEnum): string => {
  const statusLabelMap: Record<BankingValueObjectsPaymentRequestStatusEnum, string> = {
    init: i18n.t('banking.paymentRequests.status.init'),
    in_payment: i18n.t('banking.paymentRequests.status.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.status.inProgress'),
    paid: i18n.t('banking.paymentRequests.status.paid'),
  }
  return statusLabelMap[status] ?? status
}

// Helper functions to safely extract filter values
const getStringArrayFilter = (value: unknown): string[] | undefined => {
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return value.length > 0 ? value : undefined
  }
  return undefined
}

const getNumberArrayFilter = (value: unknown): number[] | undefined => {
  if (Array.isArray(value) && value.every((v) => typeof v === 'number')) {
    return value.length > 0 ? value : undefined
  }
  return undefined
}

const PaymentRequestsPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedPaymentRequests, setSelectedPaymentRequests] = useState<
    PaymentRequestFieldsFragment[]
  >([])
  const [sendToPayModalOpen, toggleSendToPayModal] = useToggle(false)

  // Track dueDate filter from DataCollection to pass to InsightCards
  const [dueDateFilter, setDueDateFilter] = useState<{ between: [string, string] } | undefined>()

  const { data, loading, error } = useQuery(PaymentRequestsPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [FEATURE_FLAG_NAME],
    },
  })

  const isFeatureEnabled =
    data?.features?.featuresConnection?.nodes.find(
      (feature: { name: string; enabled: boolean }) => feature.name === FEATURE_FLAG_NAME
    )?.enabled ?? false

  const requesterFilter = useEmployeesFilter({
    label: i18n.t('banking.paymentRequests.filters.requester'),
  })

  const goToPaymentDetail = (paymentId?: number) => {
    if (paymentId) {
      navigate(
        navigation.finance.treasury.banking.payments.paymentId.fullPath({
          id: String(paymentId),
        })
      )
    } else {
      navigate(navigation.finance.banking.payments.fullPath())
    }
  }

  // Default filter: show "init" (pending) preset
  const defaultFilters = useMemo(() => ({ status: ['init'] }), [])

  // Shared date range from localStorage (synced with Overview and other Treasury pages)
  const {
    startDate: sharedStartDate,
    endDate: sharedEndDate,
    onDateRangeChange: setSharedDateRange,
  } = useTreasuryDateRange()

  // Navigation filter for visible date picker (DateNavigator)
  // Use 'range' mode by default for bijection with Overview widget links
  // Initialize from shared localStorage date range
  const navigationFilters = useMemo(
    () => ({
      dueDate: {
        type: 'date-navigator' as const,
        defaultGranularity: 'range' as const,
        granularity: ['day', 'week', 'month', 'year', 'range'],
        defaultValue: {
          from: sharedStartDate,
          to: sharedEndDate,
        },
      },
    }),
    [sharedStartDate, sharedEndDate]
  )

  // Convert DataCollection dueDateRange filter format { from, to } to GraphQL format { between: [from, to] }
  // Note: values can be Date objects or strings (when restored from localStorage)
  // The end date uses endOfDay because due_date is a datetime column — a bare date
  // would be interpreted as midnight, missing all records during that day.
  const convertDueDateFilter = useCallback(
    (dueDateRange: { from?: Date | string; to?: Date | string } | undefined) => {
      if (!dueDateRange?.from || !dueDateRange?.to) return undefined
      const toDate = (value: Date | string): Date =>
        value instanceof Date ? value : parseISO(value)
      const start = toDate(dueDateRange.from).toISOString()
      const end = endOfDay(toDate(dueDateRange.to)).toISOString()
      const between: [string, string] = [start, end]
      return { between }
    },
    []
  )

  const handleStateChange = useCallback(
    (state: {
      navigationFilters?: { dueDate?: { value?: { from?: Date | string; to?: Date | string } } }
    }) => {
      const dueDateRange = state.navigationFilters?.dueDate?.value
      if (dueDateRange?.from && dueDateRange?.to) {
        // Update shared localStorage for sync with Overview and other Treasury pages
        const fromDate =
          dueDateRange.from instanceof Date ? dueDateRange.from : moment(dueDateRange.from).toDate()
        const toDate =
          dueDateRange.to instanceof Date ? dueDateRange.to : moment(dueDateRange.to).toDate()
        setSharedDateRange(fromDate, toDate)
      }
      setDueDateFilter(convertDueDateFilter(dueDateRange))
    },
    [convertDueDateFilter, setSharedDateRange]
  )

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: PaymentRequestsDataCollectionDocument,
      mapData: (data) =>
        data.banking.paymentRequestsConnection.edges.map(
          (edge: { node: PaymentRequestFieldsFragment }) => edge.node
        ),
      mapVariables: ({ filters, navigationFilters: navFilters }) => {
        const dueDateStatusFilter = getStringArrayFilter(filters.dueDateStatus)
        const hasDueDateStatusFilter = dueDateStatusFilter && dueDateStatusFilter.length > 0
        const statuses = filterValidStatuses(getStringArrayFilter(filters.status))
        // When filtering by due date status, exclude paid PRs (same logic as PaymentsOverdueWidget)
        const effectiveStatuses = hasDueDateStatusFilter && !statuses ? NON_PAID_STATUSES : statuses

        // Build dueDate filter from the navigation filter.
        // The end date uses endOfDay because due_date is a datetime column — a bare
        // date would be interpreted as midnight, missing all records during that day.
        const dueDateRange = navFilters?.dueDate?.value
        const toDate = (value: Date | string): Date =>
          value instanceof Date ? value : parseISO(value)
        const dueDateBetween =
          dueDateRange?.from && dueDateRange?.to
            ? {
                between: [
                  toDate(dueDateRange.from).toISOString(),
                  endOfDay(toDate(dueDateRange.to)).toISOString(),
                ],
              }
            : undefined

        const requesterIds = getNumberArrayFilter(filters.requester)

        return {
          requesterIds,
          statuses: effectiveStatuses,
          onTime: dueDateStatusFilter?.includes('on_time') || undefined,
          dueThisWeek: dueDateStatusFilter?.includes('due_this_week') || undefined,
          overdue: dueDateStatusFilter?.includes('overdue') || undefined,
          filter: dueDateBetween ? { dueDate: dueDateBetween } : undefined,
        }
      },
      paginationType: 'pages',
      perPage: 10,
      mapPagination: (data) => ({
        total: data.banking.paymentRequestsConnection.totalCount ?? 0,
        hasNextPage: data.banking.paymentRequestsConnection.pageInfo.hasNextPage ?? false,
      }),
      presets: [
        {
          label: i18n.t('banking.paymentRequests.status.init'),
          filter: { status: ['init'] },
        },
      ],
      currentFilters: defaultFilters,
      navigationFilters,
      filters: {
        requester: requesterFilter,
        status: {
          label: i18n.t('banking.paymentRequests.filters.status'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('banking.paymentRequests.status.init'), value: 'init' },
              { label: i18n.t('banking.paymentRequests.status.inPayment'), value: 'in_payment' },
              { label: i18n.t('banking.paymentRequests.status.inProgress'), value: 'in_progress' },
              { label: i18n.t('banking.paymentRequests.status.paid'), value: 'paid' },
            ],
          },
        },
        dueDateStatus: {
          label: i18n.t('banking.paymentRequests.filters.dueDateStatus'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.onTime'),
                value: 'on_time',
              },
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.dueThisWeek'),
                value: 'due_this_week',
              },
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.overdue'),
                value: 'overdue',
              },
            ],
          },
        },
      },
      itemUrl: (item: PaymentRequestFieldsFragment) =>
        navigation.finance.treasury.banking.paymentRequests.paymentRequestId.fullPath({
          paymentRequestId: item.id,
        }),
      selectable: (item: PaymentRequestFieldsFragment) =>
        item.status === 'init' ? String(item.id) : undefined,
      itemActions: (item: PaymentRequestFieldsFragment) =>
        item.status === 'init'
          ? [
              {
                label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
                icon: ArrowUp,
                type: 'primary',
                onClick: () => {
                  setSelectedPaymentRequests([item])
                  toggleSendToPayModal(true)()
                },
              },
            ]
          : undefined,
      bulkActions: () => ({
        primary: [
          {
            id: 'send-to-pay',
            label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
          },
        ],
        secondary: [],
      }),
    },
    []
  )

  const handleBulkAction = (
    action: string,
    items: { itemsStatus: readonly { checked: boolean; item: PaymentRequestFieldsFragment }[] }
  ) => {
    const selectedItems = items.itemsStatus.filter((item) => item.checked).map((item) => item.item)

    if (action === 'send-to-pay') {
      setSelectedPaymentRequests(selectedItems)
      toggleSendToPayModal(true)()
    }
  }

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.paymentRequests.columns.paymentRequest'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: row.label,
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.requester'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'person' as const,
          value: {
            firstName: row.requester?.access?.fullName ?? '-',
            lastName: '',
          },
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.items'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: String(row.lineItemsConnection?.totalCount ?? 0),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.amount'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({ amount: row.amountCents, currency: row.currency, locale }),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.dueDate'),
        render: (row: PaymentRequestFieldsFragment) =>
          convertDate(row.dueDate, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.status'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(row.status),
            status: getStatusColor(row.status),
          },
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={isFeatureEnabled}
      variant="full"
    >
      <Box flexDirection="column" gap="s16" width="full" height="full">
        <AllPaymentRequestsInsightCards
          currency={company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'}
          locale={locale}
          dueDateFilter={dueDateFilter}
        />
        <OneDataCollection
          id="payment-requests/v1"
          storage={{ features: ['filters', 'sortings', 'search'] }}
          fullHeight
          source={dataSource}
          visualizations={visualizations}
          onBulkAction={handleBulkAction}
          onStateChange={handleStateChange}
        />
      </Box>
      {sendToPayModalOpen && (
        <PayPaymentRequestsInBulkModal
          onSuccess={goToPaymentDetail}
          onClose={() => toggleSendToPayModal(false)()}
          paymentRequests={selectedPaymentRequests}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PaymentRequestsPage
